import { deviceLocal } from '@/privacy/deviceStorage';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * F-0381: the expanded work-area box's height, set by dragging its bottom edge.
 *
 * Remembered **per person, on this device** — `localStorage`, keyed by user id, not the backend
 * `IWorkAreaNavigationPreferences` namespaceOrder/workAreaOrder live on: those are synced across a
 * person's devices on purpose, and a sidebar's pixel height is a device fact (a laptop and a phone
 * do not want the same one), not an account fact. A signed-out read, or a device nobody has chosen
 * a height on yet, is the default — never another person's stored height on a shared machine,
 * because the key carries the user id.
 *
 * **The maximum is measured from the real layout, not a viewport-minus-constant formula
 * (team-lead, 2026-10-02, overturning the first cut).** A fixed `RESERVED_BELOW_VIEWPORT` pixel
 * count broke twice in one afternoon — once because it undercounted the chrome above the box, once
 * because the default itself then exceeded it on a common viewport — and would break again the
 * moment a nav line is added (F-0407 added exactly that: see
 * `docs/concept-ownership-organisations-and-instances.md` §10.10/§10.11). `containerRef`/`boxRef`
 * attach to the sidebar's own scroll column and the box's own wrapper; `measuredMaxHeight` is the
 * column's available height less whatever its *other* children (the nav lists below the box) are
 * actually taking.
 *
 * **The hook never observes the element whose height it sets (team-lead, 2026-10-02, replacing a
 * 2px "expected echo" tolerance).** An earlier cut observed `box` too, so every resize the hook
 * itself caused (a drag, a keyboard step, a reload restoring a saved height) fired the same
 * observer for `box`, which read `box.offsetHeight` fresh and recomputed the maximum from it — a
 * small, systematic timing error on each of the hook's own resizes pushed the measured maximum
 * down a little every time, converging the box to `MIN_BOX_HEIGHT`. A tolerance on "was this echo
 * close enough to ignore" is a workaround a zoom level, a fractional height or a scrollbar
 * appearing can still slip past. Removed by construction instead: `othersHeight` is read from the
 * box's *siblings* directly (`othersHeightOf`, below) — never from `box.offsetHeight` and never
 * from a `scrollHeight` that already includes the box — and only the container and those siblings
 * are ever given to the `ResizeObserver`. Nothing the hook does to `box` can feed its own
 * `ResizeObserver` an entry, so no skip condition and no tolerance is needed.
 *
 * **The chosen height and the shown height are two different values (team-lead, 2026-10-02,
 * the reload/collapse-then-reexpand drift's actual cause).** `chosenHeight` is the person's own
 * choice — set only by a drag, a keyboard step or the reset, and the only one `localStorage` ever
 * holds. `height` (what every caller reads and what is rendered) is `clampBoxHeight(chosenHeight,
 * maxHeight)`, computed fresh on every render and never written back anywhere. The previous cut
 * clamped `chosenHeight` itself against whatever `maxHeight` happened to be at the moment
 * `remeasure` ran and persisted that clamp — so an early, too-small maximum on mount (before the
 * sidebar's own layout has settled) permanently shrank the stored choice by a few pixels, and a
 * second mount (a reload, or the box's own collapse/re-expand, which unmounts and remounts this
 * region in `Layout.tsx`) measured a slightly different early value and shrank it again,
 * differently — the 256-becomes-244-or-252 drift. Keeping the two apart means an early, wrong
 * maximum can only ever affect what is *shown* for a moment; once the layout settles and `maxHeight`
 * is measured correctly, `height` returns to exactly what was chosen, because `chosenHeight` was
 * never touched.
 */

/** "A few items stay visible" (BX-3): the header row plus about three short rows. */
export const MIN_BOX_HEIGHT = 120;
/**
 * The preferred height absent a saved choice — about nine rows. Always run through
 * `clampBoxHeight` against the *measured* maximum before use (every call site here does), so it
 * can never again independently drift above what the real layout allows, the way a second
 * viewport-formula constant once did.
 */
export const DEFAULT_BOX_HEIGHT = 260;
/** One keyboard step (ArrowUp/ArrowDown) — about one row. */
const KEYBOARD_STEP = 16;
/**
 * Before the first real measurement — the very first render, before `containerRef`/`boxRef` have
 * attached, and any test that renders the hook without mounting both nodes (most of this file's
 * pure-function tests; a few of the hook tests deliberately mock layout instead). Never used once
 * a real browser has laid the sidebar out: the ref callbacks measure synchronously on mount, in the
 * same commit, before paint.
 */
const UNMEASURED_MAX_HEIGHT = 480;
/**
 * A small cushion below the measured maximum so the handle's own bottom edge never sits flush
 * against the next element — not a reserve standing in for unmeasured chrome (BX-3's old bug); the
 * chrome itself is now measured, this is only breathing room.
 */
const LAYOUT_SAFETY_PADDING = 4;

function storageKeyFor(userId: string | null): string | null {
  return userId ? `featit:workAreaBoxHeight:${userId}` : null;
}

/**
 * BX-3: the maximum never exceeds what the sidebar's scroll column actually has left over, once
 * its other children (the nav lists below the box) take what they need. `othersHeight` is what the
 * *rest* of the column occupies — `othersHeightOf`, below, is the only caller, and it never derives
 * this from the box's own size.
 */
export function measuredMaxHeight(containerClientHeight: number, othersHeight: number): number {
  return Math.max(
    MIN_BOX_HEIGHT,
    containerClientHeight - Math.max(0, othersHeight) - LAYOUT_SAFETY_PADDING,
  );
}

export function clampBoxHeight(height: number, maxHeight: number): number {
  if (height < MIN_BOX_HEIGHT) return MIN_BOX_HEIGHT;
  if (height > maxHeight) return maxHeight;
  return height;
}

/** BX-2: ArrowDown grows the box (the edge moves down), ArrowUp shrinks it; Home/End jump to the ends. */
export function nextHeightForKey(key: string, current: number, maxHeight: number): number | null {
  if (key === 'ArrowDown' || key === 'ArrowRight')
    return clampBoxHeight(current + KEYBOARD_STEP, maxHeight);
  if (key === 'ArrowUp' || key === 'ArrowLeft')
    return clampBoxHeight(current - KEYBOARD_STEP, maxHeight);
  if (key === 'Home') return MIN_BOX_HEIGHT;
  if (key === 'End') return maxHeight;
  return null;
}

/**
 * What everything in the scroll column *other than* `box` is taking — read from those other
 * elements directly, never from `box`'s own `offsetHeight` and never from a combined `scrollHeight`
 * it is already folded into (the feedback loop this file's module doc explains).
 *
 * ⚠️ **`box` is not always a direct child of `container`.** In `Layout.tsx`, `box` sits inside
 * `ProjectSelector`'s own wrapper `<section>` (the header row with its own buttons, an error
 * line, and the resize handle are all siblings of `box` *inside* that section, not of `container`
 * directly) — a flat "sum `container.children`, skip `box`" would count the section's *whole*
 * height as "other", folding `box`'s own height back in exactly the way this file exists to avoid.
 * So this walks `box`'s ancestor chain up to `container`, one level at a time, and at each level
 * sums that level's *other* children — the ones not on the path down to `box` — never `box` itself
 * or anything containing it. Every value summed is still another element's own `offsetHeight`,
 * read directly; nothing here is derived from `box`.
 */
export function othersHeightOf(container: Element, box: Element | null): number {
  let total = 0;
  let node: Element | null = box;
  while (node && node !== container && node.parentElement) {
    const parent = node.parentElement;
    for (const sibling of Array.from(parent.children)) {
      if (sibling !== node) total += (sibling as HTMLElement).offsetHeight;
    }
    node = parent;
  }
  // `box` was null, or was never found under `container` at all (not yet mounted): every direct
  // child counts, the same answer the flat sum always gave for that case.
  if (!box || node !== container) {
    total = 0;
    for (const child of Array.from(container.children)) {
      if (child === box) continue;
      total += (child as HTMLElement).offsetHeight;
    }
  }
  return total - reserveExcess(container, box);
}

/** Marks a part of the column whose size the box's maximum must not follow; see `reserveExcess`. */
const BOX_RESERVE_ATTRIBUTE = 'data-box-reserve';

/**
 * I-1596: how much of the column's other children is **not** to be counted — whatever a
 * `data-box-reserve` element is taller than its own `min-height`.
 *
 * The Views group is the last part of the sidebar and the one that arrives late: a chunk the shell
 * loads when idle, then rows the server sends. Counted at its full height, the box's maximum was
 * measured against whatever lines were there at that moment, so at 768 the box drew 250 px, then
 * settled at 120 once the group arrived, and everything below it moved. The group now stands in a
 * wrapper that holds its header line from the first paint (`min-height`) and counts as exactly that
 * — pending, loaded and expanded alike. Its rows grow below the box and the column scrolls to them
 * (the fade at the column's foot says so, I-1308); they no longer shrink the box.
 * A wrapper with no `min-height` reserves 0, so a marker alone counts nothing.
 */
function reserveExcess(container: Element, box: Element | null): number {
  let excess = 0;
  for (const reserved of Array.from(
    container.querySelectorAll<HTMLElement>(`[${BOX_RESERVE_ATTRIBUTE}]`),
  )) {
    if (box && (reserved.contains(box) || box.contains(reserved))) continue;
    const reservedHeight = Number.parseFloat(window.getComputedStyle(reserved).minHeight) || 0;
    excess += Math.max(0, reserved.offsetHeight - reservedHeight);
  }
  return excess;
}

function readStoredHeight(userId: string | null): number | null {
  const key = storageKeyFor(userId);
  if (!key || typeof window === 'undefined') return null;
  try {
    const raw = deviceLocal.getItem(key);
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    // Private browsing / a blocked storage API: read as "nothing saved", not as a crash.
    return null;
  }
}

function writeStoredHeight(userId: string | null, height: number): void {
  const key = storageKeyFor(userId);
  if (!key || typeof window === 'undefined') return;
  try {
    deviceLocal.setItem(key, String(Math.round(height)));
  } catch {
    // Private browsing / quota: the height still works for this session, just unremembered.
  }
}

export interface IResizableBoxHeight {
  /** `clampBoxHeight(chosenHeight, maxHeight)` — what to render. Never stored; see the module doc. */
  height: number;
  minHeight: number;
  maxHeight: number;
  dragging: boolean;
  /**
   * Attach to the sidebar's own scroll column (`Layout.tsx`'s `AppSidebar`) — the box's siblings
   * (the nav lists) live here too, so this is what "the layout itself" means for `measuredMaxHeight`.
   */
  containerRef: (node: HTMLElement | null) => void;
  /** Attach to the box's own wrapper — the element `height` is actually applied to. */
  boxRef: (node: HTMLElement | null) => void;
  /** BX-1/BX-6: pointer and touch both — `onPointerDown` covers each (unified Pointer Events). */
  onHandlePointerDown: (event: React.PointerEvent<HTMLDivElement>) => void;
  onHandleKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  /** BX-4: double-click the handle returns to the default height. */
  onHandleDoubleClick: () => void;
}

/**
 * Keeps a `ResizeObserver`'s observed set in sync with `container`'s current direct children,
 * excluding `box` — called on mount and whenever the column's own children change (a nav line
 * added or removed), never on a resize `box` itself causes, since `box` is never a member of this
 * set at all.
 */
function syncObservedSiblings(
  observer: ResizeObserver,
  container: Element,
  box: Element | null,
  observed: Set<Element>,
): void {
  const current = new Set(Array.from(container.children).filter((child) => child !== box));
  for (const node of observed) {
    if (!current.has(node)) {
      observer.unobserve(node);
      observed.delete(node);
    }
  }
  for (const node of current) {
    if (!observed.has(node)) {
      observer.observe(node);
      observed.add(node);
    }
  }
}

/**
 * BX-4/BX-5: one person's height, read once per mount and kept in state so collapsing and
 * re-expanding the box (which unmounts/remounts this region in `Layout.tsx`) does not lose it —
 * the read always starts from `localStorage`, not a hook default.
 */
export function useResizableBoxHeight(userId: string | null): IResizableBoxHeight {
  const [maxHeight, setMaxHeight] = useState(UNMEASURED_MAX_HEIGHT);
  // The person's own choice — never clamped against `maxHeight` here (that is `height`'s job,
  // computed below, not stored); see the module doc's "chosen vs shown" note.
  const [chosenHeight, setChosenHeight] = useState(
    () => readStoredHeight(userId) ?? DEFAULT_BOX_HEIGHT,
  );
  const [dragging, setDragging] = useState(false);
  const dragStartRef = useRef<{ pointerId: number; startY: number; startHeight: number } | null>(
    null,
  );

  // Read fresh inside stable callbacks (the observer, the ref callbacks) without forcing them to
  // be re-created — and therefore to re-observe — every time either value changes.
  const maxHeightRef = useRef(maxHeight);
  maxHeightRef.current = maxHeight;
  const userIdRef = useRef(userId);
  userIdRef.current = userId;

  // A different person signed in on this device: re-read their own stored height, not the
  // previous reader's in-memory one. Not clamped here either — see the module doc.
  useEffect(() => {
    setChosenHeight(readStoredHeight(userId) ?? DEFAULT_BOX_HEIGHT);
  }, [userId]);

  const containerNodeRef = useRef<HTMLElement | null>(null);
  const boxNodeRef = useRef<HTMLElement | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const mutationObserverRef = useRef<MutationObserver | null>(null);
  const observedSiblingsRef = useRef<Set<Element>>(new Set());

  // BX-3: re-measures from the real DOM whenever the container or one of the box's siblings
  // resizes — a window resize (the column's own clientHeight changes) and "a nav line was added"
  // (a sibling's own height changes) both go through this one path. Reads `userIdRef.current`
  // rather than taking `userId` as a dependency, so this never has to be re-created (and the
  // observer below never has to be torn down and rebuilt) just because the signed-in person
  // changed. Only sets `maxHeight` — never touches `chosenHeight` or `localStorage`; see the
  // module doc's "chosen vs shown" note for why a measurement must never write the choice.
  const remeasure = useCallback(() => {
    const container = containerNodeRef.current;
    if (!container) return;
    const containerClientHeight = container.clientHeight;
    // jsdom (and any environment that does no layout) reports 0 for everything — indistinguishable
    // from "really is zero" without this, the same rule `useElementWidth` already applies to width.
    if (containerClientHeight <= 0) return;
    const othersHeight = othersHeightOf(container, boxNodeRef.current);
    const nextMax = measuredMaxHeight(containerClientHeight, othersHeight);
    setMaxHeight((previous) => (previous === nextMax ? previous : nextMax));
  }, []);

  const getObserver = useCallback((): ResizeObserver | null => {
    if (typeof ResizeObserver === 'undefined') return null;
    if (!observerRef.current) {
      // Never given `box` to observe (see the module doc): whatever fires here is the container
      // itself or one of the box's siblings, never an echo of a resize the hook caused.
      observerRef.current = new ResizeObserver(() => remeasure());
    }
    return observerRef.current;
  }, [remeasure]);

  const containerRef = useCallback(
    (node: HTMLElement | null) => {
      const observer = getObserver();
      const previous = containerNodeRef.current;
      if (previous && observer) {
        observer.unobserve(previous);
        for (const sibling of observedSiblingsRef.current) observer.unobserve(sibling);
        observedSiblingsRef.current.clear();
      }
      mutationObserverRef.current?.disconnect();
      mutationObserverRef.current = null;
      containerNodeRef.current = node;
      if (node) {
        if (observer) {
          observer.observe(node);
          syncObservedSiblings(observer, node, boxNodeRef.current, observedSiblingsRef.current);
        }
        if (typeof MutationObserver !== 'undefined') {
          // A nav line added or removed below the box changes which siblings exist at all, which
          // a `ResizeObserver` on the existing ones cannot report by itself.
          const mutation = new MutationObserver(() => {
            if (observer)
              syncObservedSiblings(observer, node, boxNodeRef.current, observedSiblingsRef.current);
            remeasure();
          });
          mutation.observe(node, { childList: true });
          mutationObserverRef.current = mutation;
        }
      }
      remeasure();
    },
    [getObserver, remeasure],
  );

  const boxRef = useCallback(
    (node: HTMLElement | null) => {
      boxNodeRef.current = node;
      // `box` is deliberately never passed to `observer.observe` — only re-synced out of the
      // sibling set it must be excluded from (see the module doc).
      const observer = observerRef.current;
      const container = containerNodeRef.current;
      if (observer && container)
        syncObservedSiblings(observer, container, node, observedSiblingsRef.current);
      remeasure();
    },
    [remeasure],
  );

  useLayoutEffect(
    () => () => {
      observerRef.current?.disconnect();
      mutationObserverRef.current?.disconnect();
    },
    [],
  );

  const commitHeight = useCallback((next: number, persist: boolean) => {
    const clamped = clampBoxHeight(next, maxHeightRef.current);
    setChosenHeight(clamped);
    if (persist) writeStoredHeight(userIdRef.current, clamped);
  }, []);

  const onHandlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.button !== undefined && event.button !== 0) return;
      try {
        // Keeps receiving move/up even if the pointer leaves the handle mid-drag. Guarded: a
        // pointer id the browser does not recognise as active (a synthetic event, or a pointer
        // type some engines refuse capture for) throws here rather than failing the drag.
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        // Drag still works via the window-level listeners below; it just will not keep tracking
        // past the element's own bounds.
      }
      dragStartRef.current = {
        pointerId: event.pointerId,
        startY: event.clientY,
        startHeight: chosenHeight,
      };
      setDragging(true);
      // BX-6: no text selection while dragging.
      document.body.classList.add('select-none');

      const onPointerMove = (moveEvent: PointerEvent) => {
        const drag = dragStartRef.current;
        if (!drag || moveEvent.pointerId !== drag.pointerId) return;
        commitHeight(drag.startHeight + (moveEvent.clientY - drag.startY), false);
      };
      const endDrag = (upEvent: PointerEvent) => {
        const drag = dragStartRef.current;
        if (!drag || upEvent.pointerId !== drag.pointerId) return;
        commitHeight(drag.startHeight + (upEvent.clientY - drag.startY), true);
        dragStartRef.current = null;
        setDragging(false);
        document.body.classList.remove('select-none');
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup', endDrag);
        window.removeEventListener('pointercancel', endDrag);
      };
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', endDrag);
      window.addEventListener('pointercancel', endDrag);
    },
    [commitHeight, chosenHeight],
  );

  const onHandleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const next = nextHeightForKey(event.key, chosenHeight, maxHeightRef.current);
      if (next === null) return;
      event.preventDefault();
      commitHeight(next, true);
    },
    [commitHeight, chosenHeight],
  );

  const onHandleDoubleClick = useCallback(() => {
    commitHeight(DEFAULT_BOX_HEIGHT, true);
  }, [commitHeight]);

  return {
    height: clampBoxHeight(chosenHeight, maxHeight),
    minHeight: MIN_BOX_HEIGHT,
    maxHeight,
    dragging,
    containerRef,
    boxRef,
    onHandlePointerDown,
    onHandleKeyDown,
    onHandleDoubleClick,
  };
}
